import React, { useCallback, useRef } from "react";
import { NavLink } from "react-router-dom";
import { Box, Flex, Icon, Text } from "@chakra-ui/react";
import IconBox from "../Icons/IconBox";
import type { RouteConfig } from "../../types/routes";
import {
  renderRouteIconWithAnimRef,
  sidebarNavIconShellClass,
  useSidebarNavIconAnimation,
} from "./sidebarNavIconAnimation";

const ACTIVE_NAV_BG = "linear-gradient(135deg, #38BBD1, #3EEAEC)";
const ACTIVE_NAV_COLOR = "white";

export type SidebarNavRouteRowVariant = "desktop" | "drawer";

export interface SidebarNavRouteRowProps {
  routePath: string;
  prop: RouteConfig;
  variant: SidebarNavRouteRowVariant;
  isCollapsed: boolean;
  inactiveColor: string;
  activeRoute: (routeName: string) => string;
  /** Mobile drawer: close overlay after tap */
  onAfterNavigate?: () => void;
}

/**
 * Primary nav row: one stable outer shell so the motion icon is not unmounted when the route
 * flips between active and inactive (which was cutting click animations short). Icon ref +
 * pointer-down `startAnimation`; drawer close on click.
 */
export function SidebarNavRouteRow({
  routePath,
  prop,
  variant,
  isCollapsed,
  inactiveColor,
  activeRoute,
  onAfterNavigate,
}: SidebarNavRouteRowProps): React.ReactElement {
  const { iconAnimRef, linkHandlers } = useSidebarNavIconAnimation();

  const isActive = activeRoute(routePath) === "active";

  const renderStringIcon = () => {
    if (typeof prop.icon !== "string") return null;
    return <Icon>{prop.icon}</Icon>;
  };

  const label = document.documentElement.dir === "rtl" ? prop.rtlName ?? prop.name : prop.name;

  if (variant === "desktop") {
    const iconColor = isActive ? ACTIVE_NAV_COLOR : inactiveColor;
    const textColor = iconColor;

    return (
      <NavLink to={routePath} {...linkHandlers} onClick={onAfterNavigate}>
        <Box
          as="button"
          type="button"
          boxSize="initial"
          display="flex"
          justifyContent={isCollapsed ? "center" : "flex-start"}
          alignItems="center"
          bg={isActive ? ACTIVE_NAV_BG : "transparent"}
          mb={{ xl: "12px" }}
          mx={{ xl: "auto" }}
          py="12px"
          ps={isCollapsed ? "0" : "16px"}
          pe={isCollapsed ? "0" : "16px"}
          borderRadius="6px"
          w={isCollapsed ? "44px" : "100%"}
          cursor="pointer"
          _hover={isActive ? { opacity: 0.9 } : { bg: "inherit" }}
          _active={{
            transform: "none",
            borderColor: "transparent",
          }}
          _focus={{
            boxShadow: "none",
            outline: "none",
          }}
          transition="background 0.2s ease-linear, padding 0.2s ease-linear"
        >
          <Flex justifyContent={isCollapsed ? "center" : "flex-start"} alignItems="center" w="100%" position="relative">
            <Box position="relative" flexShrink={0} style={{ transition: "none" }} display="flex" alignItems="center" justifyContent="center">
              {typeof prop.icon === "string" ? (
                renderStringIcon()
              ) : (
                <Box
                  me={isCollapsed ? "0" : "12px"}
                  transition="margin 0.2s ease-linear"
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                  color={iconColor}
                  sx={{
                    pointerEvents: "none",
                    "& svg": {
                      width: "20px",
                      height: "20px",
                    },
                  }}
                >
                  {renderRouteIconWithAnimRef(prop.icon, iconAnimRef)}
                </Box>
              )}
            </Box>
            {!isCollapsed && (
              <Text
                color={textColor}
                my="auto"
                fontSize="sm"
                fontWeight={isActive ? undefined : "medium"}
                ml="0"
                flexShrink={0}
                fontFamily="'Urbanist', sans-serif"
              >
                {label}
              </Text>
            )}
          </Flex>
        </Box>
      </NavLink>
    );
  }

  // drawer / mobile — same stable shell + IconBox bg swap (icon child stays mounted)
  const drawerIconBoxBg = isActive ? "rgba(255, 255, 255, 0.2)" : ACTIVE_NAV_BG;

  return (
    <NavLink to={routePath} {...linkHandlers} onClick={onAfterNavigate}>
      <Box
        as="button"
        type="button"
        boxSize="initial"
        display="flex"
        justifyContent="flex-start"
        alignItems="center"
        bg={isActive ? ACTIVE_NAV_BG : "transparent"}
        mb={{ xl: "12px" }}
        mx={{ xl: "auto" }}
        ps="16px"
        pe="16px"
        py="12px"
        borderRadius="15px"
        w="100%"
        cursor="pointer"
        _hover={isActive ? { opacity: 0.9 } : { bg: "inherit" }}
        _active={{
          transform: "none",
          borderColor: "transparent",
        }}
        _focus={{
          boxShadow: "none",
          outline: "none",
        }}
      >
        <Flex justifyContent="flex-start" alignItems="center" w="100%" position="relative">
          <Box position="relative" flexShrink={0} display="flex" alignItems="center" justifyContent="center">
            {typeof prop.icon === "string" ? (
              renderStringIcon()
            ) : (
              <IconBox
                bg={drawerIconBoxBg}
                color="white"
                h="30px"
                w="30px"
                me="12px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                pointerEvents="none"
              >
                {renderRouteIconWithAnimRef(prop.icon, iconAnimRef)}
              </IconBox>
            )}
          </Box>
          <Text
            color={isActive ? ACTIVE_NAV_COLOR : inactiveColor}
            my="auto"
            fontSize="sm"
            fontWeight={isActive ? undefined : "medium"}
            ml="0"
            flexShrink={0}
            fontFamily="'Urbanist', sans-serif"
          >
            {label}
          </Text>
        </Flex>
      </Box>
    </NavLink>
  );
}
